import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/domain/StatusPill";
import { useGetCycleStepsQuery } from "@/store/api";
import { fmtTime } from "@/lib/format";
import { plainEnglish } from "./plainEnglish";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  cycleId: number | null;
}

export function StepTrace({ open, onClose, cycleId }: Props) {
  // Use skip until a real cycleId is passed so the query stays cold.
  const { data, isLoading } = useGetCycleStepsQuery(cycleId ?? 0, {
    skip: cycleId == null || !open,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={cycleId != null ? `Cycle #${cycleId} — step trace` : "Cycle"}
      description="Each step records a snapshot of what the bot was thinking and what happened."
      size="xl"
    >
      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (data ?? []).length === 0 ? (
        <p className="text-sm text-muted">No steps recorded.</p>
      ) : (
        <ol className="space-y-3">
          {(data ?? []).map((s) => (
            <StepRow key={s.id} step={s} />
          ))}
        </ol>
      )}
    </Dialog>
  );
}

function StepRow({ step }: { step: import("@/store/api").BackendCycleStep }) {
  const [expanded, setExpanded] = useState(false);
  const englishText = plainEnglish(step);
  return (
    <li className="border border-border rounded-md bg-bg">
      <button
        type="button"
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-bg2 transition-colors rounded-md"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 mt-0.5 shrink-0 text-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-muted" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-mono text-muted">#{step.step_order}</span>
            <span className="font-semibold">{step.name}</span>
            <StatusPill status={step.status} />
            <span className="text-xs text-muted ml-auto">{fmtTime(step.started_at)}</span>
          </div>
          <p className="text-sm text-fg/85">{englishText}</p>
          {step.summary && step.summary !== englishText && (
            <p className="text-xs text-muted mt-1 font-mono">{step.summary}</p>
          )}
        </div>
      </button>
      {expanded && step.detail_json && (
        <pre
          className={cn(
            "mx-3 mb-3 text-xs bg-bg2 border border-border rounded-md p-3",
            "overflow-x-auto whitespace-pre-wrap break-words font-mono",
          )}
        >
          {prettyJson(step.detail_json)}
        </pre>
      )}
    </li>
  );
}

function prettyJson(s: string | null | undefined): string {
  if (!s) return "";
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
